import React, { useState, useEffect, useRef } from "react";
import { showNotification } from "../utils/notificationService";
import { showPopup } from "../utils/popupService";

export default function OtpVerificationModal({
    isOpen,
    email: initialEmail,
    onVerified,
    onCancel,
    title = "Verify Email",
    requestOtpUrl,
    verifyUrl,
    resendUrl,
    resendPurpose = "signup"
}) {
    const [step, setStep] = useState(initialEmail ? "otp" : "email");
    const [email, setEmail] = useState(initialEmail || "");
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [timer, setTimer] = useState(0);
    const inputRefs = useRef([]);

    useEffect(() => {
        if (initialEmail) {
            setEmail(initialEmail);
            setStep("otp");
            setTimer(60);
        } else {
            setStep("email");
        }
    }, [initialEmail, isOpen]);

    useEffect(() => {
        let interval;
        if (isOpen && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [isOpen, timer]);

    const handleSendOtp = async () => {
        if (!email) {
            showPopup({
                type: "error",
                title: "Email Required",
                message: "Please enter your email",
                buttons: ["OK"]
            });
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(requestOtpUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to send OTP");
            }

            showPopup({
                type: "success",
                title: "Success",
                message: "OTP sent successfully!",
                buttons: ["OK"]
            });
            setStep("otp");
            setTimer(60);
        } catch (error) {
            showPopup({
                type: "error",
                title: "Error",
                message: error.message,
                buttons: ["OK"]
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (index, value) => {
        if (value.length > 1) {
            // Handle paste
            const pastedValue = value.slice(0, 6).split("");
            const newOtp = [...otp];
            pastedValue.forEach((char, i) => {
                if (index + i < 6) newOtp[index + i] = char;
            });
            setOtp(newOtp);
            const nextIndex = Math.min(index + pastedValue.length, 5);
            if (inputRefs.current[nextIndex]) {
                inputRefs.current[nextIndex].focus();
            }
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Move to next input if value is entered
        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handleVerify = async () => {
        const otpString = otp.join("");
        if (otpString.length !== 6) {
            showPopup({
                type: "error",
                title: "Invalid OTP",
                message: "Please enter all 6 digits",
                buttons: ["OK"]
            });
            return;
        }

        try {
            setLoading(true);
            const response = await fetch(verifyUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, otp: otpString }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Verification failed");
            }

            showNotification("Verified successfully!", "success");
            onVerified(data, email);
        } catch (error) {
            showNotification(error.message, "error");
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (timer > 0) return;

        try {
            setResending(true);
            const response = await fetch(resendUrl, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email, purpose: resendPurpose }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.detail || "Failed to resend OTP");
            }

            showPopup({
                type: "success",
                title: "Success",
                message: "OTP resent successfully!",
                buttons: ["OK"]
            });
            setTimer(60);
        } catch (error) {
            showPopup({
                type: "error",
                title: "Error",
                message: error.message,
                buttons: ["OK"]
            });
        } finally {
            setResending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop" style={styles.backdrop}>
            <div className="modal-card" style={styles.card}>
                <h2 style={styles.title}>{title}</h2>

                {step === "email" ? (
                    <>
                        <p style={styles.subtitle}>Enter your email to receive an OTP</p>
                        <input
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={styles.emailInput}
                            autoFocus
                        />
                        <div style={styles.buttonGroup}>
                            <button onClick={onCancel} style={{ ...styles.resendBtn, flex: 1 }}>
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                onClick={handleSendOtp}
                                disabled={loading}
                                style={{ ...styles.verifyBtn, opacity: loading ? 0.7 : 1, flex: 1 }}
                            >
                                {loading ? "Sending..." : "Send OTP"}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p style={styles.subtitle}>
                            Enter the 6-digit OTP sent to <strong>{email}</strong>
                        </p>

                        <div style={styles.otpContainer}>
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={(e) => handleChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    style={styles.otpInput}
                                    autoFocus={index === 0}
                                />
                            ))}
                        </div>

                        <div style={styles.buttonGroup}>
                            <button onClick={onCancel} style={{ ...styles.resendBtn, flex: 1 }}>
                                Cancel
                            </button>
                            <button
                                className="btn primary"
                                onClick={handleVerify}
                                disabled={loading}
                                style={{ ...styles.verifyBtn, opacity: loading ? 0.7 : 1, flex: 1 }}
                            >
                                {loading ? "Verifying..." : "Verify OTP"}
                            </button>
                        </div>

                        <div style={{ marginTop: "16px" }}>
                            <button
                                onClick={handleResendOtp}
                                disabled={timer > 0 || resending}
                                style={{
                                    ...styles.cancelBtn,
                                    opacity: (timer > 0 || resending) ? 0.5 : 1,
                                }}
                            >
                                {resending ? "Resending..." : "Resend OTP"}
                            </button>
                        </div>

                        {timer > 0 && (
                            <div style={styles.timerContainer}>
                                <span style={styles.timer}>Resend available in {timer}s</span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

const styles = {
    backdrop: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
    },
    card: {
        background: "#fff",
        padding: "32px",
        borderRadius: "16px",
        textAlign: "center",
        maxWidth: "420px",
        width: "90%",
        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
    },
    title: {
        fontSize: "24px",
        fontWeight: "700",
        color: "#111827",
        marginBottom: "8px",
    },
    subtitle: {
        fontSize: "15px",
        color: "#4b5563",
        marginBottom: "24px",
        lineHeight: "1.5",
    },
    emailInput: {
        width: "100%",
        padding: "12px",
        borderRadius: "8px",
        border: "1px solid #d1d5db",
        fontSize: "14px",
        marginBottom: "20px",
        outline: "none",
    },
    otpContainer: {
        display: "flex",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "24px",
    },
    otpInput: {
        width: "48px",
        height: "56px",
        fontSize: "24px",
        fontWeight: "600",
        textAlign: "center",
        borderRadius: "10px",
        border: "1px solid #d1d5db",
        background: "#f9fafb",
        outline: "none",
        transition: "all 0.2s ease",
        color: "#111827",
    },
    buttonGroup: {
        display: "flex",
        gap: "12px",
        width: "100%",
        marginBottom: "16px",
    },
    verifyBtn: {
        padding: "12px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
        background: "#2563eb",
        color: "#fff",
        border: "none",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
    },
    resendBtn: {
        padding: "12px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: "600",
        cursor: "pointer",
        background: "#f3f4f6",
        color: "#374151",
        border: "1px solid #d1d5db",
        transition: "all 0.2s ease",
    },
    timerContainer: {
        marginTop: "8px",
    },
    timer: {
        fontSize: "13px",
        color: "#6b7280",
    },
    cancelBtn: {
        background: "none",
        border: "none",
        color: "#6b7280",
        fontSize: "14px",
        cursor: "pointer",
        textDecoration: "underline",
    },
};
